import { comparator, compose, find, map, path, pathOr, propEq, sort } from 'ramda'
import { createSelector } from 'reselect'

import { selectCountries, selectDefaultTimezone } from '@/app/module/utils/store/selectors'

export const selectUserDetails = (user) => pathOr('', ['user', 'details', 'data'], user)
const selectUserActiveOrganizations = (user) => pathOr({}, ['user', 'details', 'data', 'orgs'], user)

export const selectUserId = createSelector(selectUserDetails, pathOr('', ['id']))

export const selectOrgs = createSelector(selectUserDetails, pathOr([], ['orgs']))

export const selectOrgId = createSelector(selectUserDetails, selectOrgs, (details, orgs) => {
  if (!orgs?.length) {
    return 0
  }

  return pathOr(0, ['activeOrgId'], details)
})

export const selectActiveOrg = createSelector(selectOrgs, selectOrgId, (orgs, orgId) =>
  find(propEq('orgId', orgId), orgs),
)

export const selectUserRole = createSelector(selectOrgId, selectUserActiveOrganizations, (activeOrgId, data) =>
  pathOr('', ['role'], find(propEq('orgId', activeOrgId), data)),
)

export const selectUserPermissions = createSelector(selectOrgId, selectUserActiveOrganizations, (activeOrgId, data) =>
  pathOr({}, ['permissions'], find(propEq('orgId', activeOrgId), data)),
)

export const selectUserTimezone = createSelector(
  selectDefaultTimezone,
  selectUserDetails,
  (defaultTz, user) => user.timezone || defaultTz,
)

export const selectUserCountry = createSelector(selectUserDetails, selectCountries, (details, countries) =>
  find(propEq('label', path(['country'], details)), countries),
)

export const selectUserOrganizations = createSelector(
  selectUserDetails,
  compose(
    map(({ name, orgId }) => ({
      label: `${name} (${orgId})`,
      value: orgId,
    })),
    sort(comparator((before, after) => before.name.toLowerCase() < after.name.toLowerCase())),
    pathOr([], ['orgs']),
  ),
)

export const selectUserTOTP = pathOr(false, ['user', 'details', 'data', 'totpEnabled'])

const ORG_TYPES = [
  {
    label: 'Business',
    value: 'B',
  },
  {
    label: 'Non-Profit',
    value: 'N',
  },
  {
    label: 'Other',
    value: 'O',
  },
]
export const selectOrganizationTypes = () => ORG_TYPES

export const selectShowOrgWarning = createSelector(
  selectActiveOrg,
  (activeOrg) => activeOrg?.permissions?.manageUsers && activeOrg?.totpAll === false,
)

export const selectShowTotpWarning = createSelector(
  selectActiveOrg,
  (activeOrg) => !['cati', 'customer_service'].includes(activeOrg?.role),
)

export const selectUserName = createSelector(selectUserDetails, ({ firstName = '', lastName = '' }) => {
  if (!firstName) {
    return lastName
  }

  if (!lastName) {
    return firstName
  }

  return `${firstName} ${lastName}`
})
